<template>
  <div
    class="vc-section vc-card vc-card--compact vc-event-trigger"
    :class="{ 'vc-event-trigger--collapsed': !expanded, 'is-disabled': disabled }"
  >
    <div
      class="vc-event-trigger__head"
      role="button"
      :tabindex="disabled ? -1 : 0"
      :title="disabled ? '' : expanded ? '收起' : '展开'"
      @click="toggleExpanded"
      @keyup.enter="toggleExpanded"
    >
      <span class="vc-event-trigger__title">事件触发器</span>
      <span class="vc-event-trigger__chevron">
        <Icon :type="expanded ? 'ios-arrow-up' : 'ios-arrow-down'" size="18" />
      </span>
    </div>

    <div v-show="expanded" class="vc-event-trigger__body">
      <div class="vc-event-trigger__toolbar">
        <Button type="text" class="vc-event-trigger__add-btn" :disabled="disabled" @click.stop="openAddModal">
          + 添加触发器
        </Button>
      </div>

      <div class="vc-event-trigger__table">
        <div class="vc-event-trigger__table-head">
          <span>触发条件</span>
          <span>动作</span>
          <span>只执行一次</span>
          <span>操作</span>
        </div>
        <div v-if="!eventTriggers.length" class="vc-event-trigger__empty">暂无触发器配置</div>
        <div
          v-for="(row, index) in eventTriggers"
          :key="row.id || 'trigger-' + index"
          class="vc-event-trigger__table-row"
        >
          <div class="vc-event-trigger__cell vc-event-trigger__cell--cond">
            <Tooltip
              :disabled="!shouldShowCellTooltip(formatConditions(row))"
              :content="formatConditions(row)"
              placement="top"
              :max-width="520"
              transfer
            >
              <span class="vc-event-trigger__cell-text">{{ formatConditionsPreview(row) }}</span>
            </Tooltip>
          </div>
          <div class="vc-event-trigger__cell vc-event-trigger__cell--action">
            <Tooltip
              :disabled="!shouldShowCellTooltip(formatAction(row))"
              :content="formatAction(row)"
              placement="top"
              :max-width="520"
              transfer
            >
              <span class="vc-event-trigger__cell-text">{{ formatActionPreview(row) }}</span>
            </Tooltip>
          </div>
          <span class="vc-event-trigger__cell-once">{{ formatOnceOnly(row) }}</span>
          <span class="vc-event-trigger__cell-actions">
            <a class="vc-event-trigger__link" :class="{ 'is-disabled': disabled }" @click.stop="openEditModal(index)">编辑</a>
            <Poptip
              :value="exclusiveConfirmKey === 'event-trigger-' + index"
              confirm
              placement="top-end"
              transfer
              title="确定删除该触发器吗？"
              ok-text="确定"
              cancel-text="取消"
              @click.native.stop
              @input="onExclusiveConfirmInput('event-trigger-' + index, $event)"
              @on-ok="removeRow(index)"
            >
              <a class="vc-event-trigger__link vc-event-trigger__link--danger" :class="{ 'is-disabled': disabled }">删除</a>
            </Poptip>
          </span>
        </div>
      </div>
    </div>

    <EventTriggerModal
      v-model="modalVisible"
      :edit-index="editingIndex"
      :initial-data="editingItem"
      :api-options="apiOptions"
      :intent-tag-name-options="intentTagNameOptions"
      :intent-name-placeholder="intentNamePlaceholder"
      @submit="onModalSubmit"
    />
  </div>
</template>

<script>
import {
  buildExternalApiOptions,
  collectIntentTagNameOptions,
  createDefaultEventTrigger,
  formatEventTriggerAction,
  formatEventTriggerConditions,
  formatEventTriggerOnceOnly,
  getIntentNameValuePlaceholder,
  normalizeEventTrigger,
} from '../../utils/eventTriggerHelpers'
import { truncateDisplayText } from '../../utils/externalApiHelpers'
import { exclusiveConfirmMixin } from '../../utils/exclusiveConfirm'
import EventTriggerModal from './EventTriggerModal.vue'

export default {
  name: 'LlmEventTriggerSection',
  mixins: [exclusiveConfirmMixin],
  components: { EventTriggerModal },
  props: {
    eventTriggers: {
      type: Array,
      required: true,
    },
    externalApisJson: {
      type: Array,
      default: () => [],
    },
    externalApisForm: {
      type: Array,
      default: () => [],
    },
    intentTag: {
      type: Object,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      expanded: false,
      modalVisible: false,
      editingIndex: -1,
      editingItem: null,
      previewMaxLength: 36,
    }
  },
  computed: {
    apiOptions() {
      return buildExternalApiOptions(this.externalApisJson, this.externalApisForm)
    },
    intentTagNameOptions() {
      return collectIntentTagNameOptions(this.intentTag)
    },
    intentNamePlaceholder() {
      return getIntentNameValuePlaceholder(this.intentTag)
    },
  },
  watch: {
    'eventTriggers.length': {
      immediate: true,
      handler(len, oldLen) {
        if (this.disabled) return
        if (len > 0 && (oldLen === undefined || oldLen === 0)) {
          this.expanded = true
        }
      },
    },
    disabled: {
      immediate: true,
      handler(locked) {
        if (locked) this.expanded = false
      },
    },
  },
  methods: {
    toggleExpanded() {
      if (this.disabled) return
      this.expanded = !this.expanded
    },
    formatConditions(row) {
      return formatEventTriggerConditions(row)
    },
    formatConditionsPreview(row) {
      return truncateDisplayText(this.formatConditions(row), this.previewMaxLength)
    },
    formatAction(row) {
      return formatEventTriggerAction(row, this.externalApisJson, this.externalApisForm)
    },
    formatActionPreview(row) {
      return truncateDisplayText(this.formatAction(row), this.previewMaxLength)
    },
    formatOnceOnly(row) {
      return formatEventTriggerOnceOnly(row)
    },
    isTextTruncated(text) {
      const raw = text != null ? String(text) : ''
      return raw.length > this.previewMaxLength
    },
    shouldShowCellTooltip(text) {
      const raw = text != null ? String(text) : ''
      return !!raw && this.isTextTruncated(raw)
    },
    openAddModal() {
      if (this.disabled) return
      this.editingIndex = -1
      this.editingItem = createDefaultEventTrigger()
      this.modalVisible = true
    },
    openEditModal(index) {
      if (this.disabled) return
      const row = this.eventTriggers[index]
      if (!row) return
      this.editingIndex = index
      this.editingItem = normalizeEventTrigger(row)
      this.modalVisible = true
    },
    onModalSubmit(item) {
      const normalized = normalizeEventTrigger(item)
      if (this.editingIndex >= 0) {
        this.$set(this.eventTriggers, this.editingIndex, normalized)
      } else {
        this.eventTriggers.push(normalized)
      }
    },
    removeRow(index) {
      if (this.disabled) return
      this.closeExclusiveConfirm('event-trigger-' + index)
      this.eventTriggers.splice(index, 1)
    },
  },
}
</script>

<style lang="scss" scoped>
@import '../voice-call/shared.scss';

.vc-section {
  margin-bottom: 16px;
}

.vc-card {
  background: #fff;
  border: 1px solid #e8eaec;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  padding: 20px;
}

.vc-card--compact {
  padding-bottom: 16px;
}

.vc-event-trigger--collapsed {
  padding-bottom: 20px;

  .vc-event-trigger__head {
    margin-bottom: 0;
  }
}

.vc-event-trigger.is-disabled {
  .vc-event-trigger__title,
  .vc-event-trigger__chevron {
    color: #bfbfbf;
  }

  .vc-event-trigger__head {
    cursor: default;
    pointer-events: none;
  }
}

.vc-event-trigger__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  min-height: 24px;
  cursor: pointer;
  user-select: none;
}

.vc-event-trigger__title {
  font-size: 15px;
  font-weight: 600;
  color: #262626;
  line-height: 24px;
}

.vc-event-trigger__chevron {
  display: inline-flex;
  align-items: center;
  color: #3e3e3e;
  flex-shrink: 0;
}

.vc-event-trigger__toolbar {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-bottom: 12px;
}

.vc-event-trigger__add-btn {
  flex-shrink: 0;
  color: #5668f0 !important;
  padding: 2px 8px !important;
  height: auto !important;

  &:hover {
    color: #3f51e8 !important;
    background: #f5f7ff !important;
  }

  &[disabled],
  &.ivu-btn-disabled {
    color: #c5c8ce !important;
    background: transparent !important;
    cursor: not-allowed !important;
  }
}

.vc-event-trigger__table {
  border: 1px solid #e8eaec;
  border-radius: 4px;
}

.vc-event-trigger__table-head,
.vc-event-trigger__table-row {
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr) 88px 96px;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  font-size: 13px;
  line-height: 1.5;
  text-align: left;
  justify-items: stretch;
}

.vc-event-trigger__table-head {
  background: #fafafa;
  color: #8c8c8c;
  font-weight: 500;
  border-bottom: 1px solid #e8eaec;
}

.vc-event-trigger__table-row {
  color: #515a6e;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }
}

.vc-event-trigger__cell {
  min-width: 0;
  width: 100%;
  overflow: hidden;

  ::v-deep .ivu-tooltip {
    display: block;
    width: 100%;
    max-width: 100%;
  }

  ::v-deep .ivu-tooltip-rel {
    display: block;
    width: 100%;
    max-width: 100%;
  }
}

.vc-event-trigger__cell-text {
  display: block;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: default;
}

.vc-event-trigger__cell-once {
  color: #515a6e;
  white-space: nowrap;
}

.vc-event-trigger__cell-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  justify-content: flex-start;
  justify-self: start;
}

.vc-event-trigger__link {
  color: #5668f0;
  cursor: pointer;
  text-decoration: none;

  &:hover {
    color: #3f51e8;
  }

  &.is-disabled {
    color: #c5c8ce;
    cursor: not-allowed;
    pointer-events: none;
  }
}

.vc-event-trigger__link--danger {
  color: #ed4014;

  &:hover {
    color: #c41d1d;
  }
}

.vc-event-trigger__empty {
  padding: 24px 12px;
  text-align: center;
  color: #bfbfbf;
  font-size: 13px;
}
</style>
